import ParseHtml from '@/lib/utils/parse-html'

import React from 'react'

import Button from '@/components/button/Button'
import { LinkProps } from '@/types/types'

/**
 * --------------------------------------------------------------------------
 * Left hand column (border top)
 *
 * @param {string} className - Additional classes to add to the column
 * @param {string} widthClass - Optionally replace the default width classes
 * --------------------------------------------------------------------------
 **/

export type MainColumnLeftProps = {
  heading?: string | null
  title?: string | null
  titleTag?: string
  titleClass?: string
  text?: string | null
  button?: LinkProps | null
  className?: string
  widthClass?: string | null
  children?: React.ReactNode | null
}

function MainColumnLeft({
  heading = null,
  title = null,
  titleTag = 'h2',
  titleClass = 'h2',
  text = null,
  button = null,
  className = '',
  widthClass = null,
  children = null,
}: MainColumnLeftProps) {
  // TODO: Remove xl-up? - too large
  widthClass = widthClass || 'ms-up:w-2/5 m-up:w-1/3 xl-up:w-1/4'

  className += ' ms:w-full ms-up:pr-m'

  return (
    <div
      className={`${className} ${widthClass} style-lists custom-table flex-shrink-0 border-t border-gradient pt-l`}>
      <div className="flex flex-col items-start gap-xl">
        {heading || title || text ? (
          <div className="flex w-full flex-col items-start gap-xxs">
            {heading && (
              <span
                className="p--small text-body mb-0 block"
                dangerouslySetInnerHTML={{ __html: heading }}></span>
            )}
            <div className="flex w-full flex-col gap-s">
              {title
                ? React.createElement(titleTag, { className: `${titleClass} mb-0` }, title)
                : null}

              {text ? <ParseHtml html={text} /> : null}
            </div>
          </div>
        ) : null}

        {button?.url ? (
          <Button
            href={button.url}
            target={button.target ?? '_self'}
            style="outline"
            className={button?.class ?? ''}>
            {button.title}
          </Button>
        ) : null}

        {children ? <div className="w-full">{children}</div> : null}
      </div>
    </div>
  )
}

/**
 * --------------------------------------------------------------------------
 * Right hand column - auto fills remaining space
 *
 * @param {string} className - Additional classes to add to the column
 * --------------------------------------------------------------------------
 **/
function MainColumnRight({
  className = '',
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={`${className} w-0 flex-grow ms:w-full`}>{children}</div>
}

function MainColumn({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="container">
        <div className="flex w-full items-start gap-l l:gap-m ms:flex-col">{children}</div>
      </div>
    </div>
  )
}

export { MainColumn, MainColumnLeft, MainColumnRight }
